"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Camera, X } from "lucide-react"
import { toast } from "react-hot-toast"

const DESCRIPTOR_LENGTH = 128
const MIN_FACE_WIDTH_RATIO = 0.22
const MIN_FACE_HEIGHT_RATIO = 0.22
const MAX_FACE_CENTER_OFFSET_RATIO = 0.18
const MIN_EYE_OPENNESS_RATIO = 0.12
const MIN_EYE_AVERAGE_RATIO = 0.14
const MIN_BRIGHTNESS = 70
const MAX_BRIGHTNESS = 190
const MIN_BLUR_SCORE = 14
const REQUIRED_STABLE_FRAMES = 3
const VALIDATION_INTERVAL_MS = 250

function getDisplayName(row) {
  if (!row) return "Unknown"
  const name = [row.firstName, row.lastName].filter(Boolean).join(" ").trim()
  if (name) return name
  return row.name || row.username || row.employeeCode || row.staffId || "Unknown"
}

function getEmployeeId(row) {
  return row?.employeeCode || row?.staffId || row?.username || "—"
}

function pointDistance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function eyeOpennessRatio(points, outerIdx, innerIdx, upperIdxList, lowerIdxList) {
  const outer = points?.[outerIdx]
  const inner = points?.[innerIdx]
  if (!outer || !inner || !Array.isArray(upperIdxList) || !Array.isArray(lowerIdxList)) return 0
  const horizontal = pointDistance(outer, inner)
  if (!horizontal) return 0
  const pairs = Math.min(upperIdxList.length, lowerIdxList.length)
  if (!pairs) return 0
  let verticalSum = 0
  let validPairs = 0
  for (let i = 0; i < pairs; i++) {
    const upper = points?.[upperIdxList[i]]
    const lower = points?.[lowerIdxList[i]]
    if (!upper || !lower) continue
    verticalSum += pointDistance(upper, lower)
    validPairs += 1
  }
  if (!validPairs) return 0
  return (verticalSum / validPairs) / horizontal
}

function evaluateFaceQuality({
  faceCount,
  box,
  leftEyeRatio,
  rightEyeRatio,
  brightnessOk = true,
  blurOk = true,
  dynamicEyeThreshold,
}) {
  if (!faceCount || !box) {
    return {
      valid: false,
      checks: {
        faceCount: faceCount || 0,
        faceInFrame: false,
        faceSizeOk: false,
        eyesOpen: false,
      },
      reason: "Face not detected clearly",
    }
  }
  const faceWidthRatio = box.width
  const faceHeightRatio = box.height
  const centerX = box.x + box.width / 2
  const centerY = box.y + box.height / 2
  const centerOffsetX = Math.abs(centerX - 0.5)
  const centerOffsetY = Math.abs(centerY - 0.5)
  const faceInFrame = box.x >= 0.02 && box.y >= 0.02 && box.x + box.width <= 0.98 && box.y + box.height <= 0.98
  const faceSizeOk = faceWidthRatio >= MIN_FACE_WIDTH_RATIO && faceHeightRatio >= MIN_FACE_HEIGHT_RATIO
  const centered = centerOffsetX <= MAX_FACE_CENTER_OFFSET_RATIO && centerOffsetY <= MAX_FACE_CENTER_OFFSET_RATIO
  const eyeAverageRatio = (leftEyeRatio + rightEyeRatio) / 2
  const minPerEye = Math.max(MIN_EYE_OPENNESS_RATIO, (dynamicEyeThreshold || 0) * 0.62)
  const minAvgEye = Math.max(MIN_EYE_AVERAGE_RATIO, dynamicEyeThreshold || 0)
  const eyesOpen =
    leftEyeRatio >= minPerEye &&
    rightEyeRatio >= minPerEye &&
    eyeAverageRatio >= minAvgEye

  if (faceCount !== 1) {
    return {
      valid: false,
      checks: {
        faceCount,
        faceInFrame,
        faceSizeOk,
        eyesOpen,
        brightnessOk,
        blurOk,
      },
      reason: faceCount > 1 ? "Only one face should be visible" : "Face not detected",
    }
  }
  if (!faceInFrame || !centered) {
    return {
      valid: false,
      checks: {
        faceCount,
        faceInFrame,
        faceSizeOk,
        eyesOpen,
        brightnessOk,
        blurOk,
      },
      reason: "Keep your face centered inside the frame",
    }
  }
  if (!faceSizeOk) {
    return {
      valid: false,
      checks: {
        faceCount,
        faceInFrame,
        faceSizeOk,
        eyesOpen,
        brightnessOk,
        blurOk,
      },
      reason: "Move closer so your face is clearly visible",
    }
  }
  if (!eyesOpen) {
    return {
      valid: false,
      checks: {
        faceCount,
        faceInFrame,
        faceSizeOk,
        eyesOpen,
        brightnessOk,
        blurOk,
      },
      reason: "Keep both eyes open",
    }
  }
  if (!brightnessOk) {
    return {
      valid: false,
      checks: {
        faceCount,
        faceInFrame,
        faceSizeOk,
        eyesOpen,
        brightnessOk,
        blurOk,
      },
      reason: "Improve lighting on your face",
    }
  }
  if (!blurOk) {
    return {
      valid: false,
      checks: {
        faceCount,
        faceInFrame,
        faceSizeOk,
        eyesOpen,
        brightnessOk,
        blurOk,
      },
      reason: "Hold still and keep camera focused",
    }
  }

  return {
    valid: true,
    checks: {
      faceCount,
      faceInFrame,
      faceSizeOk,
      eyesOpen,
      brightnessOk,
      blurOk,
    },
    reason: "",
  }
}

function extractFaceMetricsFromFaceMesh(results) {
  const faces = results?.multiFaceLandmarks || []
  if (faces.length !== 1) {
    return {
      faceCount: faces.length,
      box: null,
      leftEyeRatio: 0,
      rightEyeRatio: 0,
      brightness: 0,
      blurScore: 0,
      brightnessOk: true,
      blurOk: true,
    }
  }

  const points = faces[0]
  let minX = 1
  let minY = 1
  let maxX = 0
  let maxY = 0
  for (const p of points) {
    if (p.x < minX) minX = p.x
    if (p.y < minY) minY = p.y
    if (p.x > maxX) maxX = p.x
    if (p.y > maxY) maxY = p.y
  }

  return {
    faceCount: 1,
    box: {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    },
    leftEyeRatio: eyeOpennessRatio(points, 33, 133, [159, 160, 158], [145, 144, 153]),
    rightEyeRatio: eyeOpennessRatio(points, 263, 362, [386, 387, 385], [374, 373, 380]),
    brightness: 0,
    blurScore: 0,
    brightnessOk: true,
    blurOk: true,
  }
}

export default function RegisterFaceTab({ onRegisterSuccess }) {
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState("")
  const [isCaptureOpen, setIsCaptureOpen] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [registering, setRegistering] = useState(false)
  const [capturedPreview, setCapturedPreview] = useState("")
  const [faceApiReady, setFaceApiReady] = useState(false)
  const [qualityMessage, setQualityMessage] = useState("Position your face in front of the camera")
  const [qualityReady, setQualityReady] = useState(false)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const validationIntervalRef = useRef(null)
  const stableFramesRef = useRef(0)
  const faceMeshRef = useRef(null)
  const faceMeshResolverRef = useRef(null)
  const lastQualityRef = useRef(null)
  const lastMetricsRef = useRef(null)
  const lastLogAtRef = useRef(0)
  const cvReadyRef = useRef(false)
  const cvFailedRef = useRef(false)
  const eyeBaselineRef = useRef(0)
  const eyeBaselineSamplesRef = useRef(0)

  const selectedUser = useMemo(() => users.find((u) => u._id === selectedUserId) || null, [users, selectedUserId])

  const stopCamera = () => {
    if (validationIntervalRef.current) {
      clearInterval(validationIntervalRef.current)
      validationIntervalRef.current = null
    }
    stableFramesRef.current = 0
    eyeBaselineRef.current = 0
    eyeBaselineSamplesRef.current = 0
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    setCameraReady(false)
    setQualityReady(false)
    setQualityMessage("Position your face in front of the camera")
  }

  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [])

  const fetchUsers = async () => {
    try {
      setUsersLoading(true)
      const [teachersRes, staffRes] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.usersTeachers),
        axiosInstance.get(API_ENDPOINTS.usersStaff),
      ])
      const teachers = teachersRes.data?.data?.teachers || []
      const staff = staffRes.data?.data?.staff || []
      const merged = [...teachers, ...staff]
      setUsers(merged)
      if (!selectedUserId && merged.length) {
        setSelectedUserId(merged[0]._id)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load teachers/staff")
      setUsers([])
    } finally {
      setUsersLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const ensureFaceApiLoaded = async () => {
    if (window.faceapi?.nets?.faceRecognitionNet) return
    if (!window.__faceApiLoadPromise) {
      window.__faceApiLoadPromise = new Promise((resolve, reject) => {
        const existingTf = document.getElementById("tfjs-cdn")
        const existingFaceApi = document.getElementById("face-api-cdn")

        const loadFaceApi = () => {
          if (existingFaceApi) {
            existingFaceApi.addEventListener("load", resolve, { once: true })
            existingFaceApi.addEventListener("error", () => reject(new Error("Failed to load face-api.js")), { once: true })
            return
          }

          const faceApiScript = document.createElement("script")
          faceApiScript.id = "face-api-cdn"
          faceApiScript.src = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js"
          faceApiScript.async = true
          faceApiScript.onload = resolve
          faceApiScript.onerror = () => reject(new Error("Failed to load face-api.js"))
          document.body.appendChild(faceApiScript)
        }

        if (existingTf) {
          if (window.tf) loadFaceApi()
          else {
            existingTf.addEventListener("load", loadFaceApi, { once: true })
            existingTf.addEventListener("error", () => reject(new Error("Failed to load tfjs")), { once: true })
          }
          return
        }

        const tfScript = document.createElement("script")
        tfScript.id = "tfjs-cdn"
        tfScript.src = "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@1.7.4/dist/tf.min.js"
        tfScript.async = true
        tfScript.onload = loadFaceApi
        tfScript.onerror = () => reject(new Error("Failed to load tfjs"))
        document.body.appendChild(tfScript)
      })
    }
    await window.__faceApiLoadPromise
  }

  const ensureFaceMeshLoaded = async () => {
    if (window.FaceMesh) return
    if (!window.__faceMeshLoadPromise) {
      window.__faceMeshLoadPromise = new Promise((resolve, reject) => {
        const existing = document.getElementById("mediapipe-face-mesh-cdn")
        if (existing) {
          existing.addEventListener("load", resolve, { once: true })
          existing.addEventListener("error", () => reject(new Error("Failed to load MediaPipe FaceMesh")), { once: true })
          return
        }
        const script = document.createElement("script")
        script.id = "mediapipe-face-mesh-cdn"
        script.src = "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js"
        script.async = true
        script.onload = resolve
        script.onerror = () => reject(new Error("Failed to load MediaPipe FaceMesh"))
        document.body.appendChild(script)
      })
    }
    await window.__faceMeshLoadPromise
  }

  const ensureOpenCvLoaded = async () => {
    if (cvReadyRef.current && window.cv?.Mat) return true
    if (cvFailedRef.current) return false
    if (!window.__openCvLoadPromise) {
      window.__openCvLoadPromise = new Promise((resolve) => {
        const existing = document.getElementById("opencv-cdn")
        const finishWhenReady = () => {
          if (window.cv?.Mat) {
            cvReadyRef.current = true
            resolve(true)
            return
          }
          const startedAt = Date.now()
          const timer = setInterval(() => {
            if (window.cv?.Mat) {
              clearInterval(timer)
              cvReadyRef.current = true
              resolve(true)
              return
            }
            if (Date.now() - startedAt > 10000) {
              clearInterval(timer)
              resolve(false)
            }
          }, 100)
        }
        if (existing) {
          existing.addEventListener("load", finishWhenReady, { once: true })
          existing.addEventListener("error", () => resolve(false), { once: true })
          if (window.cv?.Mat) finishWhenReady()
          return
        }
        const script = document.createElement("script")
        script.id = "opencv-cdn"
        script.src = "https://docs.opencv.org/4.10.0/opencv.js"
        script.async = true
        script.onload = finishWhenReady
        script.onerror = () => resolve(false)
        document.body.appendChild(script)
      })
    }
    const ok = await window.__openCvLoadPromise
    if (!ok) cvFailedRef.current = true
    return ok
  }

  const analyzeFrameWithOpenCv = async (source) => {
    const cvLoaded = await ensureOpenCvLoaded()
    if (!cvLoaded || !window.cv?.Mat) {
      return { brightness: 0, blurScore: 0, brightnessOk: true, blurOk: true }
    }
    const cv = window.cv
    const tempCanvas = document.createElement("canvas")
    const width = source.videoWidth || source.width || 0
    const height = source.videoHeight || source.height || 0
    if (!width || !height) {
      return { brightness: 0, blurScore: 0, brightnessOk: false, blurOk: false }
    }
    tempCanvas.width = width
    tempCanvas.height = height
    const ctx = tempCanvas.getContext("2d")
    if (!ctx) {
      return { brightness: 0, blurScore: 0, brightnessOk: false, blurOk: false }
    }
    ctx.drawImage(source, 0, 0, width, height)
    const src = cv.imread(tempCanvas)
    const gray = new cv.Mat()
    const lap = new cv.Mat()
    const mean = new cv.Mat()
    const stddev = new cv.Mat()
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY, 0)
    cv.meanStdDev(gray, mean, stddev)
    const brightness = mean.data64F[0]
    cv.Laplacian(gray, lap, cv.CV_64F)
    cv.meanStdDev(lap, mean, stddev)
    const blurScore = stddev.data64F[0] * stddev.data64F[0]
    src.delete()
    gray.delete()
    lap.delete()
    mean.delete()
    stddev.delete()
    return {
      brightness,
      blurScore,
      brightnessOk: brightness >= MIN_BRIGHTNESS && brightness <= MAX_BRIGHTNESS,
      blurOk: blurScore >= MIN_BLUR_SCORE,
    }
  }

  const ensureFaceMeshReady = async () => {
    if (faceMeshRef.current) return faceMeshRef.current
    await ensureFaceMeshLoaded()
    const mesh = new window.FaceMesh({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
    })
    mesh.setOptions({
      maxNumFaces: 2,
      refineLandmarks: false,
      minDetectionConfidence: 0.6,
      minTrackingConfidence: 0.6,
    })
    mesh.onResults((results) => {
      const metrics = extractFaceMetricsFromFaceMesh(results)
      lastMetricsRef.current = metrics
      const quality = evaluateFaceQuality(metrics)
      lastQualityRef.current = quality
      if (faceMeshResolverRef.current) {
        const resolver = faceMeshResolverRef.current
        faceMeshResolverRef.current = null
        resolver(quality)
      }
    })
    faceMeshRef.current = mesh
    return mesh
  }

  const getFaceQuality = async (source) => {
    const mesh = await ensureFaceMeshReady()
    return new Promise(async (resolve, reject) => {
      try {
        faceMeshResolverRef.current = resolve
        await mesh.send({ image: source })
      } catch (error) {
        faceMeshResolverRef.current = null
        reject(error)
      }
    })
  }

  const getCombinedQuality = async (source) => {
    const [meshQuality, frameQuality] = await Promise.all([getFaceQuality(source), analyzeFrameWithOpenCv(source)])
    const metrics = lastMetricsRef.current || {}
    const mergedMetrics = {
      ...metrics,
      brightness: frameQuality.brightness,
      blurScore: frameQuality.blurScore,
      brightnessOk: frameQuality.brightnessOk,
      blurOk: frameQuality.blurOk,
    }
    lastMetricsRef.current = mergedMetrics
    const avgEyeRatio = ((mergedMetrics.leftEyeRatio || 0) + (mergedMetrics.rightEyeRatio || 0)) / 2
    if (mergedMetrics.box && avgEyeRatio > 0) {
      if (avgEyeRatio > eyeBaselineRef.current) {
        eyeBaselineRef.current = avgEyeRatio
      } else if (eyeBaselineRef.current > 0) {
        eyeBaselineRef.current = eyeBaselineRef.current * 0.998 + avgEyeRatio * 0.002
      }
      eyeBaselineSamplesRef.current += 1
    }
    const dynamicEyeThreshold =
      eyeBaselineSamplesRef.current >= 10 ? eyeBaselineRef.current * 0.72 : MIN_EYE_AVERAGE_RATIO
    return evaluateFaceQuality({
      faceCount: meshQuality?.checks?.faceCount || 0,
      box: mergedMetrics.box || null,
      leftEyeRatio: mergedMetrics.leftEyeRatio || 0,
      rightEyeRatio: mergedMetrics.rightEyeRatio || 0,
      brightnessOk: frameQuality.brightnessOk,
      blurOk: frameQuality.blurOk,
      dynamicEyeThreshold,
    })
  }

  const ensureFaceModelsLoaded = async () => {
    await ensureFaceApiLoaded()
    await ensureFaceMeshReady()
    ensureOpenCvLoaded()
    if (faceApiReady) return
    const MODEL_URL = "https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@0.22.2/weights"
    await Promise.all([
      window.faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
      window.faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      window.faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ])
    setFaceApiReady(true)
  }

  const openCapture = async () => {
    if (!selectedUserId) {
      toast.error("Please select teacher/staff first")
      return
    }
    try {
      setCapturedPreview("")
      setIsCaptureOpen(true)
      await ensureFaceModelsLoaded()
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
        setCameraReady(true)
        setQualityReady(false)
        setQualityMessage("Detecting face...")
        if (validationIntervalRef.current) {
          clearInterval(validationIntervalRef.current)
        }
        validationIntervalRef.current = setInterval(async () => {
          const liveVideo = videoRef.current
          if (!liveVideo || liveVideo.readyState < 2 || registering) return
          try {
            const quality = await getCombinedQuality(liveVideo)
            if (!quality.valid) {
              stableFramesRef.current = 0
              setQualityReady(false)
              setQualityMessage(quality.reason)
              return
            }
            stableFramesRef.current += 1
            const ready = stableFramesRef.current >= REQUIRED_STABLE_FRAMES
            setQualityReady(ready)
            setQualityMessage(ready ? "Ready to capture" : "Hold still...")
          } catch {
            stableFramesRef.current = 0
            setQualityReady(false)
            setQualityMessage("Face validation failed, adjust position")
          }
        }, VALIDATION_INTERVAL_MS)
      }
    } catch (err) {
      setIsCaptureOpen(false)
      stopCamera()
      toast.error(err.message || "Unable to access camera")
    }
  }

  const closeCapture = () => {
    setIsCaptureOpen(false)
    stopCamera()
  }

  const captureAndRegister = async () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || !selectedUserId) return
    try {
      setRegistering(true)
      const width = video.videoWidth
      const height = video.videoHeight
      if (!width || !height) throw new Error("Camera frame is not available")

      canvas.width = width
      canvas.height = height
      const context = canvas.getContext("2d")
      if (!context) throw new Error("Capture failed")

      context.drawImage(video, 0, 0, width, height)
      const imageDataUrl = canvas.toDataURL("image/jpeg", 0.85)
      setCapturedPreview(imageDataUrl)

      const detections = await window.faceapi
        .detectAllFaces(canvas, new window.faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
        .withFaceLandmarks()
        .withFaceDescriptors()
      if (detections.length !== 1) {
        throw new Error(detections.length > 1 ? "Only one face should be in frame" : "No face detected")
      }
      const quality = await getCombinedQuality(canvas)
      if (!quality.valid) {
        throw new Error(quality.reason)
      }
      const descriptor = detections[0]?.descriptor ? Array.from(detections[0].descriptor) : null

      if (!descriptor || descriptor.length !== DESCRIPTOR_LENGTH) {
        throw new Error("No clear face detected. Please keep face centered with good lighting.")
      }

      const registrationImage = imageDataUrl.split(",")[1] || ""
      await axiosInstance.post(`${API_ENDPOINTS.faceRecognition}/register`, {
        userId: selectedUserId,
        descriptor,
        registrationImage,
        qualityChecks: quality.checks,
      })
      toast.success("Face registered successfully")
      if (onRegisterSuccess) onRegisterSuccess()
      closeCapture()
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Registration failed")
    } finally {
      setRegistering(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Register New Face</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">Select a teacher or staff member and register their face from webcam.</p>
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <select
              className="h-10 rounded-md border px-3 text-sm"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={usersLoading || registering}
            >
              <option value="">{usersLoading ? "Loading teachers/staff..." : "Select teacher/staff"}</option>
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {`${getDisplayName(u)} (${u.role === "staff" ? "Staff" : "Teacher"} - ${getEmployeeId(u)})`}
                </option>
              ))}
            </select>
            <Button onClick={openCapture} disabled={!selectedUserId || usersLoading || registering}>
              <Camera className="h-4 w-4 mr-2" />
              Open Camera
            </Button>
          </div>
        </CardContent>
      </Card>

      {isCaptureOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                Register Face for {selectedUser ? getDisplayName(selectedUser) : "Selected User"}
              </h3>
              <Button variant="outline" onClick={closeCapture} disabled={registering}>
                <X className="h-4 w-4 mr-2" />
                Close
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border bg-gray-100 p-2">
                <div className="relative overflow-hidden rounded-lg bg-black">
                  <video ref={videoRef} className="h-72 w-full object-cover" autoPlay muted playsInline />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div
                      className={`relative h-[74%] w-[62%] rounded-[32px] border-2 ${
                        qualityReady ? "border-emerald-400" : "border-amber-300"
                      } shadow-[0_0_0_9999px_rgba(0,0,0,0.22)]`}
                    >
                      <div
                        className={`absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full ${
                          qualityReady ? "bg-emerald-400" : "bg-amber-300"
                        }`}
                      />
                    </div>
                  </div>
                  {!cameraReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-sm text-white">
                      Preparing camera...
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-600">Keep face centered and eyes open.</p>
                <p className={`mt-1 text-xs ${qualityReady ? "text-emerald-600" : "text-amber-600"}`}>{qualityMessage}</p>
              </div>
              <div className="rounded-xl border bg-gray-50 p-2">
                <div className="relative overflow-hidden rounded-lg bg-gray-200">
                  {capturedPreview ? (
                    <img src={capturedPreview} alt="Captured face preview" className="h-72 w-full object-cover" />
                  ) : (
                    <div className="flex h-72 items-center justify-center text-sm text-gray-600">Captured preview</div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-600">Preview updates after capture.</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <Button variant="outline" onClick={closeCapture} disabled={registering}>
                Cancel
              </Button>
              <Button onClick={captureAndRegister} disabled={!cameraReady || !qualityReady || registering}>
                {registering ? "Registering..." : "Capture & Register"}
              </Button>
            </div>
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </div>
      )}
    </>
  )
}
